import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Caption1, Tab, TabList, makeStyles, tokens } from '@fluentui/react-components';

import { useDocumentTitle, useSearchParams } from '@shared/hooks';

import {
  ChangeAlertBar,
  ChangeHeader,
  CompactStageIndicator,
  PrototypeRoleSwitcher,
  PrototypeStateSwitcher,
} from '../components/change';
import { ChangeV2PageScaffold } from './ChangeV2PageScaffold';
import {
  getChangeV2AssessmentPath,
  getChangeV2CompletionPath,
  getChangeV2DashboardPath,
  getChangeV2ExecutionPath,
  getChangeV2PlanPath,
  getChangeV2RequestPath,
} from '../helpers';
import { CHANGE_STATUS, LIFECYCLE_STEP } from '../models';
import { useChangeV2Store } from '../state/ChangeV2Store';
import { canEditRequest, getChangeReference } from '../utils';

// The reached task stages, shown as a compact tab strip. Only stages the change
// has actually reached appear - never a full row of disabled tabs.
type TTaskTab = { value: string; label: string; path: string };

const useStyles = makeStyles({
  controlsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  controlsLabel: { color: tokens.colorNeutralForeground3 },
  controlsGroup: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  // The task content owns its own section cards, so this is a
  // plain container - it must not add a competing surface around the form.
  task: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  taskSwitcher: { marginBottom: `-${tokens.spacingVerticalXS}` },
});

const ChangeWorkspaceLayout = () => {
  const classes = useStyles();
  const { changeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { searchParams } = useSearchParams();
  const debug = searchParams.get('debug') === 'true';
  const { getChange, role, patchRequest } = useChangeV2Store();

  const change = changeId ? getChange(changeId) : undefined;
  useDocumentTitle(change ? getChangeReference(change) : 'Change');

  if (!change) return <Navigate to={getChangeV2DashboardPath()} replace />;

  const editable = canEditRequest(role, change);

  // Show a tab for each stage the change has reached: request, then assessment /
  // planning as the record progresses. Approval is handled inside assessment.
  const tabs: TTaskTab[] = [{ value: 'request', label: 'Request', path: getChangeV2RequestPath(change.id) }];
  if (change.assessment)
    tabs.push({ value: 'assessment', label: 'Assessment', path: getChangeV2AssessmentPath(change.id) });
  if (change.assessment?.baseline || change.plan)
    tabs.push({ value: 'plan', label: 'Plan', path: getChangeV2PlanPath(change.id) });
  if (change.plan?.baseline)
    tabs.push({ value: 'execution', label: 'Execution', path: getChangeV2ExecutionPath(change.id) });
  if (change.completion || change.lifecycleStep === LIFECYCLE_STEP.COMPLETION || change.status === CHANGE_STATUS.CLOSED)
    tabs.push({ value: 'completion', label: 'Completion', path: getChangeV2CompletionPath(change.id) });

  const activeTab = location.pathname.endsWith('/assessment')
    ? 'assessment'
    : location.pathname.endsWith('/execution')
      ? 'execution'
      : location.pathname.endsWith('/completion') ||
          location.pathname.endsWith('/release') ||
          location.pathname.endsWith('/effectiveness') ||
          location.pathname.endsWith('/closure')
        ? 'completion'
        : location.pathname.endsWith('/plan')
          ? 'plan'
          : 'request';

  const handleSelectTab = (value: string) => {
    const target = tabs.find((tab) => tab.value === value);
    if (target) navigate(target.path);
  };

  return (
    <ChangeV2PageScaffold breadcrumbTail={[{ name: getChangeReference(change) }]}>
      {/* Developer-only affordances. Hidden in the normal demo; open with ?debug=true. */}
      {debug ? (
        <div className={classes.controlsBar}>
          <Caption1 className={classes.controlsLabel}>Prototype controls</Caption1>
          <div className={classes.controlsGroup}>
            <PrototypeRoleSwitcher />
            <PrototypeStateSwitcher change={change} />
          </div>
        </div>
      ) : null}

      {/* 1. Compact record header. */}
      <ChangeHeader change={change} editable={editable} onTitleChange={(title) => patchRequest(change.id, { title })} />

      {/* 2. Compact current-stage indicator (full lifecycle on demand). */}
      <CompactStageIndicator change={change} />

      {/* Conditional alerts (returned, rejected, blockers, external gates, ...). */}
      <ChangeAlertBar change={change} />

      {/* 3. Current task content - dominates the viewport. */}
      <div className={classes.task}>
        {tabs.length > 1 ? (
          <TabList
            className={classes.taskSwitcher}
            selectedValue={activeTab}
            onTabSelect={(_, data) => handleSelectTab(data.value as string)}
            aria-label="Change task"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
        ) : null}
        <Outlet />
      </div>
    </ChangeV2PageScaffold>
  );
};

export default ChangeWorkspaceLayout;
